import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Form, Spinner } from 'react-bootstrap';
import api from '../../../utils/api';
import {
  toLocalYMD,
  toLocalDateTimeInput,
  isoToLocalDateTimeInput,
  addToLocalDateTimeInput,
  isLocalDateTimeBefore,
  fromLocalDateTimeInputToISO,
  startOfAllDayISO,
  endOfAllDayISO,
} from '../../../utils/dateUtils';
import { useToast } from '../../../components/ui/ToastProvider';
import { YButton } from '../../../ui/YButton';
import { YSheet } from '../../../ui/sheet/YSheet';
import { DialogFieldsRenderer } from '../../../ui/dialog/DialogFieldsRenderer';
import { clubEventFormSchema } from '../../../ui/dialog/schemas/clubEventForm.schema';
import { eventCalendarYmd, eventLooksAllDay } from '../calendar/calendarDisplayUtils';
import { ClubEventRecurrenceFields } from './ClubEventRecurrenceFields';
import '../../../ui/dialog/yDialog.css';

function formatEventSaveError(err) {
  const data = err?.response?.data;
  if (!data) return 'Failed to save event';
  if (typeof data.error === 'string' && data.error) return data.error;
  if (Array.isArray(data.ends_at) && data.ends_at[0]) return data.ends_at[0];
  if (typeof data.ends_at === 'string') return data.ends_at;
  if (Array.isArray(data.starts_at) && data.starts_at[0]) return data.starts_at[0];
  if (typeof data.starts_at === 'string') return data.starts_at;
  const firstKey = Object.keys(data)[0];
  const firstVal = firstKey ? data[firstKey] : null;
  if (Array.isArray(firstVal) && firstVal[0]) return firstVal[0];
  if (typeof firstVal === 'string') return firstVal;
  return 'Failed to save event';
}

function extractTime(value) {
  if (!value) return '';
  const tIndex = value.indexOf('T');
  if (tIndex === -1) return '';
  return value.slice(tIndex + 1, tIndex + 6);
}

export default function ClubEventModal({
  show,
  onHide,
  clubId,
  event,
  prefillDate,
  onSaved,
  canEdit,
}) {
  const isEdit = !!event;
  const { show: showToast } = useToast();
  const [values, setValues] = useState({
    title: '',
    description: '',
    startsAt: '',
    endsAt: '',
    locationName: '',
    allDay: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [prevStartTime, setPrevStartTime] = useState('');
  const [prevEndTime, setPrevEndTime] = useState('');
  const [recurrenceFreq, setRecurrenceFreq] = useState('none');
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [recurrenceUntil, setRecurrenceUntil] = useState('');
  const [recurrenceByWeekday, setRecurrenceByWeekday] = useState([]);

  const onToggleAllDay = useCallback(
    (checked) => {
      setValues((prev) => {
        const next = { ...prev, allDay: checked };
        if (checked) {
          const startTime = extractTime(prev.startsAt) || prevStartTime || '09:00';
          const endTime = extractTime(prev.endsAt) || prevEndTime || '17:00';
          setPrevStartTime(startTime);
          setPrevEndTime(endTime);
          const startDateOnly = prev.startsAt ? prev.startsAt.slice(0, 10) : toLocalYMD(new Date());
          next.startsAt = startDateOnly;
          next.endsAt = startDateOnly;
        } else {
          const startDateOnly = prev.startsAt ? prev.startsAt.slice(0, 10) : toLocalYMD(new Date());
          const endDateOnly = prev.endsAt ? prev.endsAt.slice(0, 10) : startDateOnly;
          next.startsAt = `${startDateOnly}T${prevStartTime || '09:00'}`;
          next.endsAt = `${endDateOnly}T${prevEndTime || '17:00'}`;
        }
        return next;
      });
    },
    [prevStartTime, prevEndTime]
  );

  const setValue = useCallback(
    (name, value) => {
      if (name === 'allDay') {
        onToggleAllDay(!!value);
        return;
      }
      if (name === 'startsAt') {
        setValues((prev) => {
          const next = { ...prev, startsAt: value };
          if (prev.allDay) {
            next.endsAt = value.slice(0, 10);
            return next;
          }
          if (
            !prev.endsAt ||
            isLocalDateTimeBefore(prev.endsAt, value) ||
            prev.endsAt === prev.startsAt
          ) {
            next.endsAt = addToLocalDateTimeInput(value, { hours: 1 });
          }
          return next;
        });
        return;
      }
      setValues((prev) => ({ ...prev, [name]: value }));
    },
    [onToggleAllDay]
  );

  useEffect(() => {
    if (!show) return;
    if (event) {
      setValues({
        title: event.title || '',
        description: event.description || '',
        startsAt: eventLooksAllDay(event)
          ? eventCalendarYmd(event)
          : isoToLocalDateTimeInput(event.starts_at),
        endsAt: eventLooksAllDay(event)
          ? eventCalendarYmd(event)
          : isoToLocalDateTimeInput(event.ends_at),
        locationName: event.location_name || '',
        allDay: eventLooksAllDay(event),
      });
      setError('');
      const rec = event.recurrence || null;
      if (rec?.freq) {
        setRecurrenceFreq(rec.freq || 'none');
        setRecurrenceInterval(rec.interval || 1);
        setRecurrenceUntil(rec.until || '');
        setRecurrenceByWeekday(Array.isArray(rec.byweekday) ? rec.byweekday : []);
      } else {
        setRecurrenceFreq('none');
        setRecurrenceInterval(1);
        setRecurrenceUntil('');
        setRecurrenceByWeekday([]);
      }
    } else {
      const date = prefillDate ? new Date(prefillDate) : new Date();
      const startsAt = toLocalDateTimeInput(date);
      setValues({
        title: '',
        description: '',
        startsAt,
        endsAt: addToLocalDateTimeInput(startsAt, { hours: 1 }),
        locationName: '',
        allDay: false,
      });
      setError('');
      setRecurrenceFreq('none');
      setRecurrenceInterval(1);
      setRecurrenceUntil('');
      setRecurrenceByWeekday([]);
    }
  }, [show, event, prefillDate]);

  const toggleWeekday = (day) => {
    setRecurrenceByWeekday((prev) => {
      if (prev.includes(day)) return prev.filter((d) => d !== day);
      return [...prev, day].sort((a, b) => a - b);
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return onHide?.();
    const { title, description, startsAt, endsAt, locationName, allDay } = values;
    try {
      setSaving(true);
      setError('');
      let startsISO = null;
      let endsISO = null;
      if (allDay) {
        const startDateStr = startsAt ? startsAt.slice(0, 10) : null;
        const endDateStr = endsAt ? endsAt.slice(0, 10) : startDateStr;
        startsISO = startDateStr ? startOfAllDayISO(startDateStr) : null;
        endsISO = endDateStr ? endOfAllDayISO(endDateStr) : null;
        if (startsISO && endsISO && new Date(endsISO) < new Date(startsISO)) {
          setError('End must be after start.');
          setSaving(false);
          return;
        }
      } else {
        startsISO = startsAt ? fromLocalDateTimeInputToISO(startsAt) : null;
        endsISO = endsAt ? fromLocalDateTimeInputToISO(endsAt) : null;
        if (startsISO && endsISO && new Date(endsISO) <= new Date(startsISO)) {
          setError('End must be after start.');
          setSaving(false);
          return;
        }
      }
      let recurrencePayload = null;
      if (recurrenceFreq !== 'none') {
        recurrencePayload = {
          freq: recurrenceFreq,
          interval: Number(recurrenceInterval) || 1,
        };
        if (recurrenceFreq === 'weekly') {
          recurrencePayload.byweekday =
            recurrenceByWeekday.length > 0
              ? recurrenceByWeekday
              : [new Date(`${(startsAt || '').slice(0, 10)}T12:00:00`).getDay()];
        }
        if (recurrenceUntil) {
          recurrencePayload.until = recurrenceUntil;
        }
      }
      const payload = {
        title: title.trim(),
        description: description.trim(),
        starts_at: startsISO,
        ends_at: endsISO,
        location_name: locationName.trim(),
        all_day: allDay,
        recurrence: recurrencePayload,
      };
      if (isEdit) {
        await api.patch(`/club-events/${event.id}/`, payload);
        showToast?.({
          title: 'Event updated',
          body: 'Club event changes have been saved.',
          variant: 'success',
        });
      } else {
        await api.post(`/clubs/${clubId}/events/`, payload);
        showToast?.({
          title: 'Event created',
          body: 'New club event has been added.',
          variant: 'success',
        });
      }
      onSaved?.();
    } catch (err) {
      setError(formatEventSaveError(err));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!canEdit || !isEdit) return onHide?.();
    if (!window.confirm('Delete this event?')) return;
    try {
      setSaving(true);
      await api.delete(`/club-events/${event.id}/`);
      showToast?.({
        title: 'Event deleted',
        body: 'Club event has been removed.',
        variant: 'success',
      });
      onSaved?.();
    } catch {
      setError('Failed to delete event');
    } finally {
      setSaving(false);
    }
  };

  const formatDisplayDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDisplayTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const isAllDayDisplay = () => eventLooksAllDay(event);

  const modalTitle = canEdit
    ? isEdit
      ? clubEventFormSchema.titleEdit
      : clubEventFormSchema.titleAdd
    : clubEventFormSchema.titleView;

  const eventKicker = canEdit ? (isEdit ? 'EDIT' : 'NEW') : 'EVENT';
  const editFooter = (
    <>
      <YButton type="button" variant="secondary" size="sm" onClick={onHide}>
        Cancel
      </YButton>
      <YButton type="submit" form="club-event-form" size="sm" disabled={saving}>
        {saving ? (
          <>
            <Spinner size="sm" animation="border" className="me-1" />
            Saving…
          </>
        ) : (
          'Save Event'
        )}
      </YButton>
    </>
  );
  const deleteFooterStart = isEdit ? (
    <YButton
      type="button"
      variant="danger"
      size="sm"
      onClick={onDelete}
      disabled={saving}
    >
      {saving ? <Spinner size="sm" animation="border" className="me-1" /> : null}
      Delete
    </YButton>
  ) : null;

  if (!canEdit) {
    return (
      <YSheet
        show={show}
        onHide={onHide}
        kicker="EVENT"
        title={modalTitle}
        testId="club-event-modal"
        footer={
          <YButton type="button" variant="secondary" size="sm" onClick={onHide}>
            Close
          </YButton>
        }
      >
        <h4 className="y-dialog__detail-title">{event?.title || values.title || 'Event'}</h4>
        <p className="y-dialog__detail-meta">
          {isAllDayDisplay() ? (
            <>{formatDisplayDate(event?.starts_at)} (All day)</>
          ) : event?.ends_at &&
            new Date(event.ends_at).toDateString() !== new Date(event.starts_at || '').toDateString() ? (
            <>
              {formatDisplayDate(event?.starts_at)} {formatDisplayTime(event?.starts_at)} –{' '}
              {formatDisplayDate(event?.ends_at)} {formatDisplayTime(event?.ends_at)}
            </>
          ) : (
            <>
              {formatDisplayDate(event?.starts_at)}
              {event?.starts_at ? ` at ${formatDisplayTime(event.starts_at)}` : ''}
            </>
          )}
        </p>
        {event?.recurrence?.freq ? (
          <p className="y-dialog__detail-meta">
            Repeats {event.recurrence.freq}
            {event.recurrence.interval ? ` every ${event.recurrence.interval}` : ''}
            {event.recurrence.until ? ` until ${event.recurrence.until}` : ''}
          </p>
        ) : null}
        {event?.location_name || values.locationName ? (
          <p className="y-dialog__detail-meta">Location: {event?.location_name || values.locationName}</p>
        ) : null}
        {event?.description || values.description ? (
          <p className="y-dialog__detail-block">{event?.description || values.description}</p>
        ) : null}
      </YSheet>
    );
  }

  return (
    <YSheet
      show={show}
      onHide={onHide}
      kicker={eventKicker}
      title={modalTitle}
      testId="club-event-modal"
      error={error}
      footerStart={deleteFooterStart}
      footer={editFooter}
    >
      <Form id="club-event-form" onSubmit={onSubmit}>
        <DialogFieldsRenderer
          schema={clubEventFormSchema}
          values={values}
          setValue={setValue}
          idPrefix="club-event"
          fieldMins={{
            endsAt: values.allDay ? values.startsAt?.slice(0, 10) : values.startsAt || undefined,
          }}
          slots={{
            recurrence: (
              <ClubEventRecurrenceFields
                freq={recurrenceFreq}
                interval={recurrenceInterval}
                until={recurrenceUntil}
                byWeekday={recurrenceByWeekday}
                onFreqChange={setRecurrenceFreq}
                onIntervalChange={setRecurrenceInterval}
                onUntilChange={setRecurrenceUntil}
                onToggleWeekday={toggleWeekday}
              />
            ),
          }}
        />
      </Form>
    </YSheet>
  );
}

ClubEventModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  clubId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  event: PropTypes.object,
  prefillDate: PropTypes.instanceOf(Date),
  onSaved: PropTypes.func,
  canEdit: PropTypes.bool,
};
